import React, {PureComponent} from "react"
import {cloneDeep} from "lodash"
import {MutationFn, MutationResult} from "react-apollo"

import {User} from "@irati/types"
import Card from "@irati/ui/src/Layout/Card"
import TransferFromTenantToRewardMutation from "@irati/wallet/apollo/mutation/TransferFromTenantToReward"
import TenantWalletBalanceQuery, {TenantWalletBalanceQueryResult} from "@irati/wallet/apollo/query/TenantWallet"
import TransferType from "@irati/wallet/enums/TransferType"
import {subtractCostFromBalance} from "@irati/payments/utils"
import History from "@irati/utils/history"
import tracking from "@irati-tracking/wallet/tenantWallet"
import TenantWalletTransferType from "@irati/wallet/enums/TenantWalletTransferType"
import {FormattedMessage} from "@irati/utils/lang"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {H4} from "@irati/ui/src/Typography"
import authentication from "@irati/auth"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {returnUserTextWithAndOrComma} from "@irati/wallet/helpers"
import ConfirmationPopup from "@irati-web/reward/src/components/ConfirmationPopup"

import Amount from "../Amount"
import {CreditTransferConfirmationPopup} from "../WalletPopups"
import Button from "./Button"
import Recipients from "./Recipients"
import Totals from "./Totals"

const DEFAULT_AMOUNT = "10.00"

interface Props {
  id: string
}

interface State {
  amount: string
  recipients: User[]
  showConfirmation: boolean
  showPreConfirmationPopup: boolean
}

class TenantWalletTransfer extends PureComponent<Props, State> {
  state: State = {
    amount: DEFAULT_AMOUNT,
    recipients: [],
    showConfirmation: false,
    showPreConfirmationPopup: false
  }

  onCompleted = () => {
    tracking.completeTenantWalletTransferCredit()
    this.setState({showConfirmation: true})
  }

  addRecipient = (user: User) => {
    tracking.selectManager()
    const recipients = cloneDeep(this.state.recipients)
    recipients.push(user)
    this.setState({recipients})
  }

  removeRecipient = (user: User) => {
    const recipients = cloneDeep(this.state.recipients).filter((u) => u.uuid !== user.uuid)
    this.setState({recipients})
  }

  updateAmount = (amount: string) => {
    tracking.updateTransferValue()
    this.setState({amount})
  }

  handleClose = () => {
    History.push({
      pathname: "/wallet/tenant"
    })
  }

  handleRepeatAgain = () => {
    this.setState({showConfirmation: false, recipients: [], amount: DEFAULT_AMOUNT})
  }

  handleClick = (mutate: MutationFn) => {
    tracking.clickTransferCreditCTA()
    mutate()
  }

  handleHide = () => {
    this.setState({showPreConfirmationPopup: false})
  }

  handleContinue = () => {
    this.setState({showPreConfirmationPopup: true})
  }

  buildUsers = () => {
    const {recipients} = this.state
    return recipients.map(this.userToNameAndEmail)
  }

  userToNameAndEmail = (user: User, index: number) => {
    const {recipients} = this.state
    const text = returnUserTextWithAndOrComma(
      recipients.length,
      index,
      "wallet.tenant.transfer.confirmationPopup.text1.user",
      "wallet.tenant.transfer.confirmationPopup.text1.userWithComma",
      "wallet.tenant.transfer.confirmationPopup.text1.userWithAnd"
    )

    return (
      <div className="text-center">
        <FormattedMessage
          id={text}
          values={{
            name: firstnameLastname(user),
            email: user.email
          }}
        />
      </div>
    )
  }

  render() {
    const {amount, recipients, showConfirmation, showPreConfirmationPopup} = this.state
    const {id} = this.props

    const totalAmount = Number(amount) * recipients.length
    return (
      <Card>
        <TenantWalletBalanceQuery>
          {({data}: TenantWalletBalanceQueryResult) => {
            const {availableBalance, currency} = data.tenantWallet
            const variables = {
              amount,
              currency,
              recipientIds: recipients.map((r) => r.uuid),
              tenantId: authentication.userTenant().uuid,
              walletType: TransferType.REWARDS
            }

            const remainderBalance = subtractCostFromBalance(
              {
                amount: availableBalance,
                currency
              },
              {
                amount: Number(amount) * recipients.length,
                currency
              }
            )

            const insufficientFunds = remainderBalance.amount < 0

            return (
              <>
                <TransferFromTenantToRewardMutation onCompleted={this.onCompleted} variables={variables}>
                  {(mutate: MutationFn, apolloState: MutationResult) => {
                    return (
                      <>
                        <Amount
                          amount={amount}
                          id={id}
                          name="TenantWalletTransferAmount"
                          label="wallet.tenant.transfer.amount.label"
                          updateAmount={this.updateAmount}
                        />

                        <Recipients addRecipient={this.addRecipient} recipients={recipients} removeRecipient={this.removeRecipient} />

                        <Totals
                          amount={totalAmount}
                          currency={currency}
                          recipients={recipients}
                          updatedBalance={remainderBalance}
                          wallet={data.tenantWallet}
                        />

                        <Button
                          apolloState={apolloState}
                          disabled={recipients.length === 0 || apolloState.loading || insufficientFunds}
                          recipients={recipients}
                          onClick={this.handleContinue}
                        />
                        <ConfirmationPopup
                          title="wallet.tenant.transfer.confirmationPopup.title"
                          topText={
                            <>
                              <FormattedMessage
                                id="wallet.tenant.transfer.confirmationPopup.text1.top"
                                values={{
                                  credit: formatCurrency({currencyCode: currency, value: totalAmount, isCents: false})
                                }}
                              />
                              {this.buildUsers()}
                              <FormattedMessage id="wallet.tenant.transfer.confirmationPopup.text1.bottom" />
                            </>
                          }
                          bottomText={
                            <FormattedMessage
                              id="wallet.tenant.transfer.confirmationPopup.text2"
                              values={{
                                boldText: (
                                  <H4 as="strong">
                                    <FormattedMessage id="wallet.tenant.transfer.confirmationPopup.text2Bold" />
                                  </H4>
                                )
                              }}
                            />
                          }
                          show={showPreConfirmationPopup}
                          onHide={this.handleHide}
                          onClick={() => {
                            this.handleHide()
                            this.handleClick(mutate)
                          }}
                          backButtonText="platform.button.goBack"
                          continueButtonText={<FormattedMessage id="wallet.tenant.transfer.button.allocate" />}
                        />
                      </>
                    )
                  }}
                </TransferFromTenantToRewardMutation>
                {showConfirmation && (
                  <CreditTransferConfirmationPopup
                    show={showConfirmation}
                    handleClose={this.handleClose}
                    handleRepeatAgain={this.handleRepeatAgain}
                    recipients={recipients}
                    transferType={TenantWalletTransferType.TRANSFER}
                    amount={amount}
                    totalAmount={totalAmount}
                    remainingAmount={remainderBalance.amount}
                    currency={currency}
                  />
                )}
              </>
            )
          }}
        </TenantWalletBalanceQuery>
      </Card>
    )
  }
}

export default TenantWalletTransfer
